<template>
  <Teleport to="body">
    <Transition name="gs-fade">
      <div v-if="open" class="gs-overlay" @mousedown.self="close">
        <div class="gs-box" role="dialog" aria-label="Busca global">

          <div class="gs-input-row">
            <svg class="gs-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
            </svg>
            <input
              ref="inputRef"
              v-model="query"
              class="gs-input"
              placeholder="Buscar conversas, contatos, protocolo..."
              autocomplete="off"
              spellcheck="false"
              @keydown.esc="close"
              @keydown.down.prevent="moveFocus(1)"
              @keydown.up.prevent="moveFocus(-1)"
              @keydown.enter.prevent="selectFocused"
            />
            <button v-if="query" class="gs-clear" @click="query = ''; inputRef?.focus()">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round">
                <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
              </svg>
            </button>
            <kbd v-else class="gs-shortcut">Ctrl K</kbd>
          </div>

          <div class="gs-body">
            <div v-if="loading" class="gs-state">
              <span class="gs-spinner"></span>
              Buscando...
            </div>

            <div v-else-if="!query || query.length < 2" class="gs-state gs-state--hint">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <span>Digite nome, telefone ou protocolo pra buscar conversas e contatos</span>
            </div>

            <div v-else-if="!hasResults" class="gs-state">
              Nenhum resultado para "<strong>{{ query }}</strong>"
            </div>

            <div v-else class="gs-results">
              <template v-if="results.conversations.length">
                <div class="gs-group-label">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                  </svg>
                  Conversas
                </div>
                <button
                  v-for="(conv, i) in results.conversations"
                  :key="'cv' + conv.id"
                  class="gs-item"
                  :class="{ 'gs-item--focus': focusedIndex === i }"
                  @click="openConv(conv)"
                  @mouseenter="focusedIndex = i"
                >
                  <span class="gs-avatar">{{ convInitials(conv) }}</span>
                  <span class="gs-item-body">
                    <span class="gs-item-title">{{ convDisplayName(conv) }}</span>
                    <span class="gs-item-sub">
                      <template v-if="conv.teamName">{{ conv.teamName }}</template>
                      <span class="gs-badge" :class="'gs-badge--' + statusKey(conv.status)">{{ STATUS_LABEL[conv.status] ?? '—' }}</span>
                    </span>
                    <span v-if="convPhone(conv) || conv.flowName || conv.protocol" class="gs-item-extra">
                      <span v-if="conv.protocol" class="gs-item-extra-protocol" title="Protocolo">#{{ conv.protocol }}</span>
                      <span v-if="convPhone(conv)">{{ convPhone(conv) }}</span>
                      <span v-if="conv.flowName" class="gs-item-extra-fluxo">{{ conv.flowName }}</span>
                    </span>
                  </span>
                  <svg class="gs-item-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </template>

              <template v-if="results.contacts.length">
                <div class="gs-group-label" :style="results.conversations.length ? 'margin-top:10px' : ''">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                  </svg>
                  Contatos
                </div>
                <button
                  v-for="(contact, i) in results.contacts"
                  :key="'ct' + contact.id"
                  class="gs-item"
                  :class="{ 'gs-item--focus': focusedIndex === results.conversations.length + i }"
                  @click="openContact(contact)"
                  @mouseenter="focusedIndex = results.conversations.length + i"
                >
                  <span class="gs-avatar gs-avatar--contact">{{ initials(contact.name) }}</span>
                  <span class="gs-item-body">
                    <span class="gs-item-title">{{ contact.name }}</span>
                    <span class="gs-item-sub">{{ contact.phone ? fmtPhone(contact.phone) : (contact.email || '—') }}</span>
                  </span>
                  <svg class="gs-item-arrow" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <polyline points="9 18 15 12 9 6"/>
                  </svg>
                </button>
              </template>
            </div>
          </div>

          <div class="gs-footer">
            <span><kbd>↑↓</kbd> navegar</span>
            <span><kbd>↵</kbd> abrir</span>
            <span><kbd>Esc</kbd> fechar</span>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRouter } from 'vue-router'
import { globalSearchOpen, closeGlobalSearch } from '@/composables/useGlobalSearch'
import { requestOpenChat } from '@/composables/useChatTrigger'
import { useChatRailMode } from '@/composables/useChatRailMode'
import { fmtPhone } from '@/utils/contactsFormat'
import { hiddenFluxoIdsCp, isFluxoHiddenCp } from '@/composables/useHiddenFluxosCp'
import { listConversations, STATUS_LABEL, type ConversationListItem } from '@/services/wootdashConversations'
import { searchContacts, type CpContact } from '@/services/wootdashContacts'

// Busca global (Ctrl K) no WootDash: conversas e contatos pelo texto (nome, telefone…).
// A conversa abre no painel lateral (ConvDrawer) — o popup antigo dependia do n8n.
const open  = globalSearchOpen
const store  = useStore()
const router = useRouter()
const { mode: railMode } = useChatRailMode()
const token = computed<string>(() => store.getters.authUser?.wootdash_token ?? '')

const query        = ref('')
const loading      = ref(false)
const focusedIndex = ref(0)
const inputRef     = ref<HTMLInputElement | null>(null)
const results      = ref<{ conversations: ConversationListItem[], contacts: CpContact[] }>({ conversations: [], contacts: [] })

const hasResults  = computed(() => results.value.conversations.length > 0 || results.value.contacts.length > 0)
const totalItems  = computed(() => results.value.conversations.length + results.value.contacts.length)

// Classe do selo (as cores já existem pra open/pending/resolved).
function statusKey(status: number): string {
  return status === 0 ? 'open' : status === 1 ? 'pending' : 'resolved'
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null

watch(open, (val) => {
  if (val) {
    query.value = ''
    results.value = { conversations: [], contacts: [] }
    focusedIndex.value = 0
    nextTick(() => inputRef.value?.focus())
  }
})

watch(query, (val) => {
  if (debounceTimer) clearTimeout(debounceTimer)
  focusedIndex.value = 0
  if (!val || val.length < 2) {
    results.value = { conversations: [], contacts: [] }
    loading.value = false
    return
  }
  loading.value = true
  debounceTimer = setTimeout(() => doSearch(val), 300)
})

let searchSeq = 0

async function doSearch(q: string) {
  if (!token.value) { loading.value = false; return }
  const seq = ++searchSeq
  try {
    const [convs, contacts] = await Promise.all([
      listConversations(token.value, { search: q.trim(), pageSize: 8, excludeFlowIds: hiddenFluxoIdsCp.value }),
      searchContacts(token.value, q.trim(), 1, 5),
    ])
    if (seq !== searchSeq) return // resposta de uma busca já obsoleta (query mudou nesse meio-tempo)
    // Fluxo escondido (robô/IA) não aparece pro agente em nenhuma tela — nem aqui.
    results.value = {
      conversations: convs.conversations.filter(c => !isFluxoHiddenCp(c.flowId)).slice(0, 5),
      contacts: contacts.contacts,
    }
  } catch {
    if (seq !== searchSeq) return
    results.value = { conversations: [], contacts: [] }
  } finally {
    if (seq === searchSeq) loading.value = false
  }
}

function close() { closeGlobalSearch() }

function openConv(conv: ConversationListItem) {
  railMode.value = 'client' // o painel de conversa fica escondido se o trilho estiver no chat interno
  requestOpenChat(conv.id)
  close()
}

function openContact(contact: CpContact) {
  router.push({ path: '/contacts', query: { q: contact.name } })
  close()
}

function moveFocus(dir: number) {
  if (totalItems.value === 0) return
  focusedIndex.value = (focusedIndex.value + dir + totalItems.value) % totalItems.value
}

function selectFocused() {
  if (!hasResults.value) return
  const convCount = results.value.conversations.length
  if (focusedIndex.value < convCount) {
    openConv(results.value.conversations[focusedIndex.value])
  } else {
    openContact(results.value.contacts[focusedIndex.value - convCount])
  }
}

function initials(name?: string): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/)
  if (parts.length === 1) return parts[0][0].toUpperCase()
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

function convDisplayName(conv: ConversationListItem): string {
  return conv.contactName?.trim() || conv.contactPhone?.trim() || 'Conversa'
}

function convPhone(conv: ConversationListItem): string | null {
  const phone = conv.contactPhone?.trim()
  return phone ? fmtPhone(phone) : null
}

function convInitials(conv: ConversationListItem): string {
  const name = conv.contactName?.trim()
  if (name) return initials(name)
  const phone = conv.contactPhone?.trim()
  return phone ? phone.slice(-2) : '#'
}
</script>

<style scoped>
.gs-overlay {
  position: fixed;
  inset: 0;
  z-index: 9000;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: flex-start;
  justify-content: center;
  padding-top: 10vh;
}

.gs-box {
  width: 100%;
  max-width: 560px;
  background: #1a2332;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
  display: flex;
  flex-direction: column;
}

.gs-input-row {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.07);
}

.gs-search-icon {
  color: rgba(255, 255, 255, 0.35);
  flex-shrink: 0;
}

.gs-input {
  flex: 1;
  background: none;
  border: none;
  outline: none;
  font-size: 15px;
  color: #fff;
  font-family: inherit;
}

.gs-input::placeholder {
  color: rgba(255, 255, 255, 0.3);
}

.gs-shortcut {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.12);
  color: rgba(255, 255, 255, 0.35);
  border-radius: 5px;
  padding: 2px 6px;
  font-size: 11px;
  font-family: inherit;
  white-space: nowrap;
  flex-shrink: 0;
}

.gs-clear {
  background: none;
  border: none;
  cursor: pointer;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  flex-shrink: 0;
  transition: color 0.15s, background 0.15s;
}

.gs-clear:hover {
  color: #fff;
  background: rgba(255, 255, 255, 0.1);
}

.gs-body {
  min-height: 80px;
  max-height: 400px;
  overflow-y: auto;
}

.gs-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 20px;
  color: rgba(255, 255, 255, 0.45);
  font-size: 13px;
  text-align: center;
  min-height: 100px;
}

.gs-state--hint {
  gap: 10px;
}

.gs-spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.15);
  border-top-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  animation: gs-spin 0.7s linear infinite;
  flex-shrink: 0;
}

@keyframes gs-spin {
  to { transform: rotate(360deg); }
}

.gs-results {
  padding: 8px;
}

.gs-group-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 10.5px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: rgba(255, 255, 255, 0.3);
  padding: 6px 8px 4px;
}

.gs-item {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 8px 10px;
  background: none;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  text-align: left;
  transition: background 0.12s;
  color: inherit;
  font-family: inherit;
}

.gs-item:hover,
.gs-item--focus {
  background: rgba(255, 255, 255, 0.07);
}

.gs-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: rgba(188, 12, 36, 0.25);
  color: #f87171;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.gs-avatar--contact {
  background: rgba(59, 130, 246, 0.2);
  color: #93c5fd;
}

.gs-item-body {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.gs-item-title {
  font-size: 13px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.gs-item-sub {
  font-size: 11.5px;
  color: rgba(255, 255, 255, 0.4);
  display: flex;
  align-items: center;
  gap: 4px;
  flex-wrap: wrap;
}

.gs-item-extra {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.32);
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
  margin-top: 1px;
}

.gs-item-extra > span + span::before {
  content: '·';
  margin-right: 6px;
  opacity: 0.6;
}

.gs-item-extra-protocol {
  color: #fcd34d;
  font-variant-numeric: tabular-nums;
}

.gs-item-extra-fluxo {
  color: #93c5fd;
}

.gs-item-arrow {
  color: rgba(255, 255, 255, 0.2);
  flex-shrink: 0;
}

.gs-badge {
  padding: 1px 6px;
  border-radius: 10px;
  font-size: 10.5px;
  font-weight: 600;
}

.gs-badge--open     { background: rgba(16, 185, 129, 0.15); color: #6ee7b7; }
.gs-badge--pending  { background: rgba(245, 158, 11, 0.15); color: #fcd34d; }
.gs-badge--resolved { background: rgba(99, 102, 241, 0.15); color: #a5b4fc; }
.gs-badge--snoozed  { background: rgba(156, 163, 175, 0.12); color: #9ca3af; }

.gs-footer {
  display: flex;
  gap: 16px;
  padding: 10px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.07);
  font-size: 11px;
  color: rgba(255, 255, 255, 0.28);
}

.gs-footer kbd {
  background: rgba(255, 255, 255, 0.07);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  padding: 1px 5px;
  font-family: inherit;
  font-size: 10.5px;
  color: rgba(255, 255, 255, 0.4);
  margin-right: 4px;
}

/* Transition */
.gs-fade-enter-active,
.gs-fade-leave-active {
  transition: opacity 0.15s ease;
}
.gs-fade-enter-active .gs-box,
.gs-fade-leave-active .gs-box {
  transition: transform 0.15s ease, opacity 0.15s ease;
}
.gs-fade-enter-from,
.gs-fade-leave-to {
  opacity: 0;
}
.gs-fade-enter-from .gs-box,
.gs-fade-leave-to .gs-box {
  transform: translateY(-8px);
  opacity: 0;
}
</style>
